import {
  Flex,
  type FlexProps,
  Input,
  type InputProps,
  Stack,
  Text,
} from '@mantine/core';
import type React from 'react';
import {
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useIsMdBreakpoint } from '../../../../hooks';

type PinProps = InputProps &
  Pick<
    React.ComponentPropsWithoutRef<'input'>,
    | 'value'
    | 'onChange'
    | 'onBlur'
    | 'onKeyDown'
    | 'onPaste'
    | 'onFocus'
    | 'placeholder'
    | 'maxLength'
    | 'disabled'
  >;

const Pin = forwardRef<HTMLInputElement, PinProps>((props, ref) => (
  <Input
    ref={ref as any}
    type="text"
    inputMode="numeric"
    autoComplete="one-time-code"
    size="lg"
    sx={{
      '.mantine-Input-input': {
        fontSize: 16,
        width: 50,
        padding: 0,
        textAlign: 'center',
      },
    }}
    {...props}
  />
));
Pin.displayName = 'Pin';

export type OptInputProps = Omit<FlexProps, 'onChange'> & {
  length?: number;
  value?: string;
  onChange?: (value: string) => void;
  onComplete?: (value: string) => void;
  disabled?: boolean;
  autoFocus?: boolean;
  inputProps?: Omit<InputProps, 'value' | 'onChange' | 'type'>;
  error?: string;
};

/**
 * OptInput component
 * A component for entering one-time passwords.
 *
 * @constructor
 */
export const OptInput = forwardRef<HTMLDivElement, OptInputProps>(
  (
    {
      length = 6,
      value,
      onChange,
      onBlur,
      onComplete,
      disabled,
      autoFocus,
      inputProps,
      error,
      ...flexProps
    },
    ref,
  ) => {
    const [internalValue, setInternalValue] = useState('');
    const currentValue = value ?? internalValue;
    const isMobile = !useIsMdBreakpoint();

    const chars = useMemo(() => {
      const safe = (currentValue || '').replace(/\D/g, '').slice(0, length);
      const arr = Array.from({ length }, (_, i) => safe[i] ?? '');
      return arr as string[];
    }, [currentValue, length]);

    const inputsRef = useRef<(HTMLInputElement | null)[]>([]);

    const focusAt = useCallback(
      (idx: number) => {
        const clamped = Math.max(0, Math.min(length - 1, idx));
        const el = inputsRef.current[clamped];
        if (el) {
          // Focus underlying input element inside Mantine Input
          // Some Mantine versions forward ref to input element
          // If it does not, focusing will be a no-op, but typically works.
          (el as any).focus?.();
        }
      },
      [length],
    );

    const setAll = useCallback(
      (next: string) => {
        if (onChange) onChange(next);
        if (value === undefined) setInternalValue(next);
        if (onComplete && next.length === length && !next.includes(''))
          onComplete(next);
      },
      [length, onChange, onComplete, value],
    );

    const updateChar = useCallback(
      (index: number, ch: string) => {
        const clean = ch.replace(/\D/g, '');
        const c = clean.charAt(0) || '';
        const prev = chars.join('');
        const arr = [...chars];
        arr[index] = c;
        const next = arr.join('').replace(/\s/g, '');
        if (next !== prev) setAll(next);
      },
      [chars, setAll],
    );

    /**
     * Handle change event, update char and move focus to the next field
     * @param index
     */
    const handleChange =
      (index: number) => (e: React.KeyboardEvent<HTMLInputElement>) => {
        const val = e.key;
        const el = e.target as HTMLInputElement;
        if (el.value) {
          el.select();
        }

        updateChar(index, val);
        focusAt(index + 1);
      };

    const handleKeyDown =
      (index: number) => (e: React.KeyboardEvent<HTMLInputElement>) => {
        const { key } = e;
        if (Number.isInteger(Number(key))) {
          handleChange(index)(e);
        } else if (key === 'Backspace') {
          if (chars[index]) {
            // clear current char
            updateChar(index, '');
          } else if (index > 0) {
            // move left and clear
            e.preventDefault();
            focusAt(index - 1);
            // small timeout to ensure focus shift then clear
            setTimeout(() => updateChar(index - 1, ''), 0);
          }
        } else if (key === 'ArrowLeft') {
          e.preventDefault();
          focusAt(index - 1);
        } else if (key === 'ArrowRight') {
          e.preventDefault();
          focusAt(index + 1);
        }
      };

    const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
      e.preventDefault();
      const pasted = e.clipboardData.getData('text') || '';
      const clean = pasted.replace(/\D/g, '').slice(0, length);
      setAll(clean);
      const nextIndex = Math.min(clean.length, length - 1);
      focusAt(nextIndex);
    };

    /**
     * Handle focus event, when focused field contains value, select this value in input
     * @param e
     */
    const handleFocus = (e: React.FocusEvent) => {
      const el = e.target as HTMLInputElement;
      if (el.value) {
        el.select();
      }
    };

    useEffect(() => {
      if (autoFocus) {
        const firstEmpty = chars.findIndex(c => !c);
        focusAt(firstEmpty === -1 ? 0 : firstEmpty);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
      <Stack spacing={4}>
        <Flex gap={isMobile ? 4 : 16} align="center" {...flexProps}>
          {Array.from({ length }).map((_, i) => (
            <Pin
              key={`pin-${i + 1}`}
              value={chars[i]}
              onChange={() => null}
              disabled={disabled}
              onBlur={onBlur}
              onKeyDown={handleKeyDown(i)}
              onPaste={handlePaste}
              onFocus={handleFocus}
              placeholder=""
              maxLength={1}
              ref={el => {
                inputsRef.current[i] = el;
                if (i === 0 && typeof ref === 'function' && el) ref(el);
                if (i === 0 && ref && typeof ref === 'object')
                  // eslint-disable-next-line no-param-reassign
                  (ref as any).current = el;
              }}
              {...inputProps}
            />
          ))}
        </Flex>
        {error && (
          <Text color="red.0" fz="md">
            {error}
          </Text>
        )}
      </Stack>
    );
  },
);
